import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface CartItem {
  id: string;
  name: string;
  brand: string;
  dailyRate: number;
  weeklyRate: number;
  category: "camera" | "lens" | "action" | "drone" | "accessory" | "kit";
  quantity: number;
  rentalPeriod: "daily" | "weekly";
  isKit?: boolean;
  kitProducts?: Array<{
    id: string;
    name: string;
    brand: string;
  }>;
}

interface CartContextType {
  items: CartItem[];
  itemCount: number;
  totalAmount: number;
  addToCart: (product: Omit<CartItem, 'quantity' | 'rentalPeriod'>, rentalPeriod?: "daily" | "weekly") => void;
  addKitToCart: (kitId: string, kitName: string, kitDailyPrice: number, kitWeeklyPrice: number, kitProducts: Array<{id: string, name: string, brand: string}>, rentalPeriod?: "daily" | "weekly") => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

const CART_STORAGE_KEY = 'telephotorental-cart';

export const CartProvider = ({ children }: CartProviderProps) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        setItems(parsedCart);
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
  }, []);

  // Save cart to localStorage whenever items change
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [items]);

  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  
  const totalAmount = items.reduce((total, item) => {
    const rate = item.rentalPeriod === "weekly" ? item.weeklyRate : item.dailyRate;
    return total + (rate * item.quantity);
  }, 0);

  const addToCart = (product: Omit<CartItem, 'quantity' | 'rentalPeriod'>, rentalPeriod: "daily" | "weekly" = "daily") => {
    setItems(currentItems => {
      const existingItem = currentItems.find(item => item.id === product.id && item.rentalPeriod === rentalPeriod);
      
      if (existingItem) {
        return currentItems.map(item =>
          item.id === product.id && item.rentalPeriod === rentalPeriod
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...currentItems, { ...product, quantity: 1, rentalPeriod }];
      }
    });
  };

  const addKitToCart = (kitId: string, kitName: string, kitDailyPrice: number, kitWeeklyPrice: number, kitProducts: Array<{id: string, name: string, brand: string}>, rentalPeriod: "daily" | "weekly" = "daily") => {
    const kitItem: CartItem = {
      id: kitId,
      name: kitName,
      brand: "Kit Bundle",
      dailyRate: kitDailyPrice,
      weeklyRate: kitWeeklyPrice,
      category: "kit",
      quantity: 1,
      rentalPeriod,
      isKit: true,
      kitProducts
    };

    setItems(currentItems => {
      const existingItem = currentItems.find(item => item.id === kitId && item.rentalPeriod === rentalPeriod);
      
      if (existingItem) {
        return currentItems.map(item =>
          item.id === kitId && item.rentalPeriod === rentalPeriod
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...currentItems, kitItem];
      }
    });
  };

  const removeFromCart = (itemKey: string) => {
    setItems(currentItems => currentItems.filter(item => `${item.id}-${item.rentalPeriod}` !== itemKey));
  };

  const updateQuantity = (itemKey: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemKey);
      return;
    }
    
    setItems(currentItems =>
      currentItems.map(item =>
        `${item.id}-${item.rentalPeriod}` === itemKey ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing cart from localStorage:', error);
    }
  };

  const value: CartContextType = {
    items,
    itemCount,
    totalAmount,
    addToCart,
    addKitToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    isCartOpen,
    setIsCartOpen,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};